import { roundToCents } from "@/lib/money";
import { toNumber } from "@/lib/utils/query-helpers";

interface CreditApplicationBalanceRow {
  amount_applied?: unknown;
  is_reversal?: unknown;
}

function getAppliedDelta(row: CreditApplicationBalanceRow): number {
  const amount = toNumber(row.amount_applied) ?? 0;
  return row.is_reversal === true ? -amount : amount;
}

export function buildAppliedAmountMap<
  TKey extends string,
  TRow extends CreditApplicationBalanceRow & Partial<Record<TKey, unknown>>,
>(applications: readonly TRow[], idKey: TKey): Map<string, number> {
  const appliedById = new Map<string, number>();

  for (const application of applications) {
    const id = application[idKey];
    if (typeof id !== "string" || id.length === 0) {
      continue;
    }

    appliedById.set(id, roundToCents((appliedById.get(id) ?? 0) + getAppliedDelta(application)));
  }

  return appliedById;
}

export function sumAppliedAmount<T extends CreditApplicationBalanceRow>(
  applications: readonly T[],
): number {
  return applications.reduce(
    (sum, application) => roundToCents(sum + getAppliedDelta(application)),
    0,
  );
}
/**
 * The account's credit balance: the ledger formula
 * `SUM(amount) - SUM(non-reversal) + SUM(reversals)`, applied here as the sum of
 * each credit's already-netted available balance.
 *
 * Deliberately unfloored, matching `get_account_credit_balance` and
 * `getAccountRelated`. Clamping each credit at zero would make this disagree
 * with the credits list, which never clamps, and would hide an over-applied
 * credit behind a healthy-looking total — the anomaly the
 * `gl_credit_applications` guard exists to surface. See
 * `openspec/specs/credit-applications/spec.md` ("Balance Calculation").
 */
export function sumAvailableCredit(
  credits: { available_balance?: number | null }[] | null | undefined,
): number {
  return (credits ?? []).reduce(
    (total, credit) => roundToCents(total + (credit.available_balance ?? 0)),
    0,
  );
}

/**
 * The set of application ids that `rows` reverse.
 *
 * A reversal row carries `reverses_application_id` pointing at the application
 * it undoes; every other row leaves it null. Reading the target off each
 * reversal names the originals that already have an undo, which is what a list
 * needs to stop offering "Reverse" a second time — a repeat the ledger guard
 * rejects, so the only question is whether the UI asks.
 */
export function collectReversedApplicationIds(
  rows: ReadonlyArray<{ reverses_application_id?: string | null }>,
): Set<string> {
  const reversedApplicationIds = new Set<string>();
  for (const row of rows) {
    if (row.reverses_application_id) {
      reversedApplicationIds.add(row.reverses_application_id);
    }
  }
  return reversedApplicationIds;
}

/**
 * Stamps `is_reversed` onto each application from a set of already-reversed ids.
 *
 * Callers own the id set because its completeness is what makes the flag
 * trustworthy: a set built from a paged slice would understate reversal state
 * for any application whose reversal fell outside the page, re-enabling the
 * reverse action on a row the RPC will only reject.
 */
export function markReversedApplications<T extends { id: string }>(
  applications: readonly T[],
  reversedApplicationIds: ReadonlySet<string>,
): (T & { is_reversed: boolean })[] {
  return applications.map((application) => ({
    ...application,
    is_reversed: reversedApplicationIds.has(application.id),
  }));
}
